<template>
	<div
		class="flex flex-grow flex-col"
		:style="{
			borderColor: 'transparent',
			boxSizing: 'border-box',
			borderStyle: 'solid',
			borderTopWidth: 'env(safe-area-inset-top)',
			borderBottomWidth: 'env(safe-area-inset-bottom)',
			borderLeftWidth: 'env(safe-area-inset-left)',
			borderRightWidth: 'env(safe-area-inset-right)'
		}"
	>
		<NuxtPwaManifest />
		<NuxtLoadingIndicator />
		<NuxtLayout>
			<NuxtPage />
		</NuxtLayout>
		<div
			class="fixed bottom-0 left-0 right-0 z-[999999]"
			:style="{
				height: 'env(safe-area-inset-bottom)',
				background: companyStore.domainCompany?.color || '#EA6939'
			}"
		></div>
		<div
			class="fixed left-0 right-0 top-0 z-[999999]"
			:style="{
				height: 'env(safe-area-inset-top)',
				background: companyStore.domainCompany?.color || '#EA6939'
			}"
		></div>
		<div
			class="fixed bottom-0 left-0 top-0 z-[999999]"
			:style="{
				width: 'env(safe-area-inset-left)',
				background: companyStore.domainCompany?.color || '#EA6939'
			}"
		></div>
		<div
			class="fixed bottom-0 right-0 top-0 z-[999999]"
			:style="{
				width: 'env(safe-area-inset-right)',
				background: companyStore.domainCompany?.color || '#EA6939'
			}"
		></div>
		<span
			v-if="userStore.user?.is_demo || userStore.user?.is_company_demo"
			class="text-white border-white pointer-events-none fixed z-[20] block border-s-2 border-t-2 bg-gradient-to-tr from-red-600 to-red-500 px-6 py-3 text-xl font-black shadow-2xl duration-300 rounded-ts-xl end-safe bottom-safe"
		>
			{{ $t("demo") }}
		</span>
	</div>
</template>

<script setup lang="ts">
const app = useNuxtApp()
const route = useRoute()
const loadingIndicator = useLoadingIndicator()
const companyStore = useCompanyStore()
const userStore = useUserStore()
// Access the shared WebSocket state without creating additional watchers
const isChatWebSocketConnected = useState<boolean>("ws-is-connected", () => false)
useNtfy()

const { play: playBeep } = useSound("/audio/beep.mp3")

const documentVisibility = useDocumentVisibility()

watch(
	() => [route.path, route.query],
	(newRoute, oldRoute) => {
		if (newRoute[0] != oldRoute[0]) return
		loadingIndicator.finish()
	}
)

useIntervalFn(() => {
	userStore.elapsedTime++
}, 1_000)

useIntervalFn(async () => {
	if (companyStore.companyId && companyStore.company && userStore.user?.id) {
		try {
			const oldCompany = { ...companyStore.company }
			const unseenCounts = await companyStore.getUnseenCounts()
			if (oldCompany.id != companyStore.company?.id) return

			let hasNewNotification = false

			// Check for new reward payrolls
			if ((unseenCounts.total_unseen_reward_payrolls || 0) > (oldCompany.total_unseen_reward_payrolls || 0)) {
				app.$toast.info({
					message: app.$i18n.t(
						"you_have_new_reward_payrolls",
						{
							n: unseenCounts.total_unseen_reward_payrolls
						},
						unseenCounts.total_unseen_reward_payrolls || 0
					)
				})
				hasNewNotification = true
			}

			// Check for new payrolls
			if ((unseenCounts.total_unseen_payrolls || 0) > (oldCompany.total_unseen_payrolls || 0)) {
				app.$toast.info({
					message: app.$i18n.t(
						"you_have_new_payrolls",
						{ n: unseenCounts.total_unseen_payrolls },
						unseenCounts.total_unseen_payrolls || 0
					)
				})
				hasNewNotification = true
			}

			// Check for new messages (only show notification if WebSocket is not connected)
			if (
				(unseenCounts.total_unseen_messages || 0) > (oldCompany.total_unseen_messages || 0) &&
				(!isChatWebSocketConnected.value || documentVisibility.value == "hidden")
			) {
				app.$toast.info({
					message: app.$i18n.t(
						"you_have_new_messages",
						{ n: unseenCounts.total_unseen_messages },
						unseenCounts.total_unseen_messages || 0
					)
				})
				hasNewNotification = true
			}

			// Check for new mails
			if ((unseenCounts.total_unseen_mails || 0) > (oldCompany.total_unseen_mails || 0)) {
				app.$toast.info({
					message: app.$i18n.t(
						"you_have_new_mails",
						{ n: unseenCounts.total_unseen_mails },
						unseenCounts.total_unseen_mails || 0
					)
				})
				hasNewNotification = true
			}

			// Check for new performance reviews
			if (
				(unseenCounts.total_unseen_performance_reviews || 0) >
				(oldCompany.total_unseen_performance_reviews || 0)
			) {
				app.$toast.info({
					message: app.$i18n.t(
						"you_have_new_performance_reviews",
						{
							n: unseenCounts.total_unseen_performance_reviews
						},
						unseenCounts.total_unseen_performance_reviews || 0
					)
				})
				hasNewNotification = true
			}

			// Check for new announcements
			if ((unseenCounts.total_unseen_announcements || 0) > (oldCompany.total_unseen_announcements || 0)) {
				app.$toast.info({
					message: app.$i18n.t(
						"you_have_new_announcements",
						{
							n: unseenCounts.total_unseen_announcements
						},
						unseenCounts.total_unseen_announcements || 0
					)
				})
				hasNewNotification = true
			}

			// Check for new disciplinary notes
			if (
				(unseenCounts.total_unseen_disciplinary_notes || 0) > (oldCompany.total_unseen_disciplinary_notes || 0)
			) {
				app.$toast.info({
					message: app.$i18n.t(
						"you_have_new_disciplinary_notes",
						{
							n: unseenCounts.total_unseen_disciplinary_notes
						},
						unseenCounts.total_unseen_disciplinary_notes || 0
					)
				})
				hasNewNotification = true
			}

			if (hasNewNotification && documentVisibility.value == "hidden") {
				playBeep()
			}
		} catch (e) {}
	}
}, 15_000)

watch(
	() => app.$pwa?.needRefresh,
	() => {
		app.$toast.info({
			message: app.$i18n.t("app_new_content_available_message"),
			buttons: [[app.$i18n.t("reload"), () => app.$pwa?.updateServiceWorker(true), false]]
		})
	}
)

useHead(() => ({
	title: companyStore?.domainCompany?.title || app.$i18n.t("app_name"),
	htmlAttrs: {
		translate: "no",
		dir: app.$i18n.localeProperties.value.dir,
		lang: app.$i18n.localeProperties.value.code
	},
	bodyAttrs: {},
	script: [
		{
			innerHTML: `window.addEventListener(
				"beforeinstallprompt",
				(e) => {
					e.preventDefault()
					window.beforeInstallPrompt = e
				}
			)`
		}
	],
	meta: [
		{ charset: "utf-8" },
		{
			name: "viewport",
			content:
				"width=device-width, initial-scale=1.0, maximum-scale=1, user-scalable=0, interactive-widget=resizes-content, viewport-fit=cover"
		},
		{
			hid: "description",
			name: "description",
			content: companyStore?.domainCompany?.description || app.$i18n.t("app_description")
		},
		{ name: "format-detection", content: "telephone=no" },
		{ name: "msapplication-TileColor", content: companyStore?.domainCompany?.color || "#EA6939" },
		{ name: "theme-color", content: companyStore?.domainCompany?.color || "#EA6939" },
		{ name: "fontiran.com:license", content: app.$config.public.fontIranCode }
	],
	link: [
		{
			rel: "manifest",
			href:
				"/api/manifest.json" +
				(companyStore.domainCompany?.id
					? `?company_id=${companyStore.domainCompany?.id}&updated=${companyStore?.domainCompany?.updated}`
					: "")
		},
		{
			rel: "canonical",
			href: companyStore?.domainCompany
				? companyStore.domainCompany.baseurl + route.path.replace(/\/$/, "")
				: app.$config.public.siteUrl + route.path.replace(/\/$/, "")
		},
		!companyStore?.domainCompany
			? {
					rel: "apple-touch-icon",
					size: "180x180",
					href: "/apple-touch-icon.png"
				}
			: {},
		!companyStore?.domainCompany
			? {
					rel: "icon",
					type: "image/png",
					sizes: "32x32",
					href: "/favicon-32x32.png"
				}
			: {},
		!companyStore?.domainCompany
			? {
					rel: "icon",
					type: "image/png",
					sizes: "16x16",
					href: "/favicon-16x16.png"
				}
			: {},
		!companyStore?.domainCompany?.image_id && !companyStore?.domainCompany?.maskable_image_id
			? { rel: "icon", type: "image/x-icon", href: "/favicon.ico" }
			: {
					rel: "icon",
					type: "image/png",
					href: companyStore?.domainCompany?.maskable_image_id
						? app.$config.public.baseUrl + companyStore.domainCompany?.maskable_image_thumb1
						: app.$config.public.baseUrl + companyStore.domainCompany?.image_thumb1
				},
		!companyStore?.domainCompany
			? {
					rel: "mask-icon",
					href: "/safari-pinned-tab.svg",
					color: companyStore?.domainCompany?.color || "#EA6939"
				}
			: {}
	]
}))

onMounted(() => {
	// pwa
	if (app.$pwa?.offlineReady) {
		app.$toast.success({ message: app.$i18n.t("offline_ready_message") })
	}
})
</script>

<style>
.iziToast-wrapper-bottomLeft,
.iziToast-wrapper-bottomCenter {
	bottom: env(safe-area-inset-bottom) !important;
}
</style>
